// HUD overlay. Launched by GameScene and kept alive for the whole run (it
// survives room restarts). Polls session.ctx every frame (null between rooms)
// and getRun(); transient feedback (banners, toasts, damage, boss phases)
// arrives through the event bus.
//
// Layout (1920×1080): bottom-left deck = vitals · abilities · weapons;
// top-left = sector / route / scrap / synergies; top-right = challenge (+FPS);
// top-centre = boss bar; centre = banners; bottom-centre = toasts + prompt.
// The middle of the screen is left clear for gameplay.

import Phaser from 'phaser';
import { TEX, iconKey } from '../art/keys';
import { SCENES, SECTORS_PER_RUN, ULTIMATE_COST } from '../config/constants';
import { COLORS } from '../config/palette';
import type { GameContext } from '../core/contracts';
import { Subscriptions } from '../core/events';
import { getRun, type RunState } from '../core/run';
import { save } from '../core/save';
import { session } from '../core/session';
import type { MechId, SynergyId, WeaponId } from '../core/types';
import { MECHS } from '../data/mechs';
import { SECTORS } from '../data/sectors';
import { reactorColorFor } from '../data/unlocks';
import { AbilityDial, EquipPanel, VitalsHud, WeaponPanel, deckPanel, type EquipRowState, type WeaponSlotState } from '../ui/hud/deck';
import { describeItem } from '../systems/loot/describe';
import { EQUIP_SLOTS } from '../core/types';
import { BannerQueue, DamageVignette, InteractPrompt, ToastStack } from '../ui/hud/messages';
import { BossBar, ChallengeTracker, StatusHud } from '../ui/hud/top';
import { Panel, UI, syncCursor, txt } from '../ui/kit';

const DECK_Y = 930;

export class UIScene extends Phaser.Scene {
  private subs = new Subscriptions();
  private vitals!: VitalsHud;
  private sig!: AbilityDial;
  private ult!: AbilityDial;
  private weapons!: WeaponPanel;
  private gear!: EquipPanel;
  private gearVersion = -1;
  private status!: StatusHud;
  private tracker!: ChallengeTracker;
  private boss!: BossBar;
  private banners!: BannerQueue;
  private toasts!: ToastStack;
  private vignette!: DamageVignette;
  private prompt!: InteractPrompt;
  private fps!: Phaser.GameObjects.Text;
  private reticle!: Phaser.GameObjects.Image;
  private abilityPanel!: Panel;
  private mechId: MechId | null = null;
  private accent: number = COLORS.volt;
  private maxHp = 100;
  private hpFrac = 1;
  private synergyTimer = 0;
  private fpsTimer = 0;
  private reticleKick = 0;
  private warned = false;
  private first = true;

  constructor() {
    super(SCENES.UI);
  }

  create(): void {
    this.first = true;
    this.warned = false;
    this.mechId = null;
    const run = this.currentRun();
    const mechId: MechId = run?.mechId ?? 'volt';
    this.accent = this.accentFor(mechId);
    const mech = MECHS[mechId];

    this.vignette = new DamageVignette(this);

    this.vitals = new VitalsHud(this, 32, DECK_Y, mech.name, this.accent);
    this.abilityPanel = deckPanel(this, 520, DECK_Y, 212, 118, this.accent);
    this.sig = new AbilityDial(this, 570, DECK_Y + 46, 34, iconKey.signature(mech.signature), this.accent, 'signature', 'cooldown');
    this.ult = new AbilityDial(this, 670, DECK_Y + 46, 34, iconKey.ultimate(mech.ultimate), COLORS.ultBar, 'ultimate', 'charge');
    this.weapons = new WeaponPanel(this, 744, DECK_Y, this.accent);
    this.gear = new EquipPanel(this, 744 + WeaponPanel.W + 12, DECK_Y, this.accent);

    this.status = new StatusHud(this, 32, 28, this.accent);
    // Versus has no route / sector / scrap economy: the run status panel stays hidden.
    if (this.scene.isActive(SCENES.VERSUS)) this.status.setVisible(false);
    this.tracker = new ChallengeTracker(this, UI.W - 32, 44);
    this.boss = new BossBar(this, UI.W / 2, 26);
    this.banners = new BannerQueue(this, 300);
    this.banners.reduced = () => save.settings.reducedFlashing;
    this.toasts = new ToastStack(this, 838);
    this.prompt = new InteractPrompt(this, 892);
    this.fps = txt(this, UI.W - 32, 10, '', { font: 'mono', size: 18, weight: 600, color: COLORS.uiDim, originX: 1 }).setVisible(false);

    const retKey = this.textures.exists(TEX.reticle) ? TEX.reticle : this.fallbackReticle();
    this.reticle = this.add.image(-100, -100, retKey).setDepth(1000).setVisible(false);

    if (run) {
      this.status.initScrap(run.scrap);
      this.applyMech(run.mechId);
    }

    this.subscribe();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.subs.dispose());
  }

  // ───────────────────────────── Events ─────────────────────────────

  private subscribe(): void {
    const reduced = (): boolean => save.settings.reducedFlashing;
    this.subs
      .on('ui:banner', (e) => this.banners.push({ title: e.title, subtitle: e.subtitle, color: e.color ?? this.accent }))
      .on('ui:toast', (e) => this.toasts.push(e.text, e.color ?? COLORS.uiText, e.duration ?? 2600))
      .on('player:damaged', (e) => {
        this.vignette.flash(e.amount / Math.max(1, this.maxHp), reduced());
        this.vitals.hurt();
      })
      .on('player:shieldBreak', () => this.vitals.shieldBreak(reduced()))
      .on('scrap:changed', (e) => this.status.setScrap(e.total, e.delta))
      .on('challenge:started', (e) => this.tracker.event('started', e.id, e.text))
      .on('challenge:completed', (e) => this.tracker.event('completed', e.id, e.text))
      .on('challenge:failed', (e) => this.tracker.event('failed', e.id, e.text))
      .on('boss:spawned', (e) => {
        const b = e.boss;
        this.boss.show({ name: b.name, title: b.title, hp: b.hp, maxHp: b.maxHp, armor: b.armorHp, phase: b.phase, phaseCount: b.phaseCount });
      })
      .on('boss:phase', () => this.boss.phaseFlash())
      .on('boss:defeated', () => this.boss.hide())
      .on('synergy:activated', () => (this.synergyTimer = 0))
      .on('synergy:deactivated', () => (this.synergyTimer = 0))
      .on('inventory:changed', () => (this.synergyTimer = 0))
      .on('weapon:fired', (e) => {
        if (!e.secondary) this.reticleKick = 1;
      })
      .on('settings:changed', () => this.fps.setVisible(save.settings.showFps));
  }

  // ───────────────────────────── Frame ─────────────────────────────

  update(_time: number, deltaMs: number): void {
    const dt = Math.min(deltaMs / 1000, 0.1);
    const reduced = save.settings.reducedFlashing;
    const ctx = session.ctx;
    const run = ctx?.run ?? this.currentRun();
    syncCursor(this);

    if (run && run.mechId !== this.mechId) this.applyMech(run.mechId);
    try {
      if (run) this.pollRun(run, ctx, dt);
      if (ctx) this.pollContext(ctx, run!, dt, reduced);
      else this.idle();
    } catch (err) {
      // A system that is still a stub (or mid-teardown) must never take the HUD down.
      if (!this.warned) {
        this.warned = true;
        console.warn('[hud] poll failed', err);
      }
    }
    this.first = false;

    this.vitals.tick(dt, reduced);
    this.sig.tick(dt, reduced);
    this.ult.tick(dt, reduced);
    this.weapons.tick(dt, reduced);
    this.status.tick(dt);
    this.tracker.tick(dt);
    this.boss.tick(dt);
    this.vignette.tick(dt, this.hpFrac, reduced);

    this.fpsTimer -= dt;
    if (save.settings.showFps) {
      if (!this.fps.visible) this.fps.setVisible(true);
      if (this.fpsTimer <= 0) {
        this.fpsTimer = 0.5;
        this.fps.setText(`${Math.round(this.game.loop.actualFps)} FPS`);
      }
    } else if (this.fps.visible) this.fps.setVisible(false);
    // Challenge tracker drops below the FPS readout when both are visible.
    this.tracker.y = this.fps.visible ? 44 : 32;
  }

  private currentRun(): RunState | null {
    return session.ctx?.run ?? getRun();
  }

  private accentFor(mechId: MechId): number {
    try {
      return reactorColorFor(mechId);
    } catch {
      return MECHS[mechId]?.reactorColor ?? COLORS.volt;
    }
  }

  /** Mech changed (new run): recolour every accent and swap ability icons. */
  private applyMech(mechId: MechId): void {
    this.mechId = mechId;
    const mech = MECHS[mechId];
    this.accent = this.accentFor(mechId);
    this.vitals.setIdentity(mech.name, this.accent);
    this.sig.setColor(this.accent);
    this.sig.setIconKey(iconKey.signature(mech.signature));
    this.ult.setIconKey(iconKey.ultimate(mech.ultimate));
    this.weapons.setAccent(this.accent);
    this.gear.setAccent(this.accent);
    this.status.setAccent(this.accent);
    this.abilityPanel.setStyle({ accent: this.accent });
  }

  /** Worn equipment: rebuilt only when the inventory changes. */
  private pollGear(run: RunState): void {
    const inv = run.inventory;
    if (inv.version === this.gearVersion) return;
    this.gearVersion = inv.version;
    const rows: EquipRowState[] = EQUIP_SLOTS.map((slot) => {
      const it = inv.equipment[slot];
      if (!it) return { uid: null, name: '', bonus: '', color: COLORS.steel3, icon: null };
      const d = describeItem(it);
      // The numeric stat bonuses read at a glance; items without any show their tagline.
      const stats = d.lines.filter((l) => /^[+-]\d/.test(l)).slice(0, 2).map(shortStat);
      return { uid: it.uid, name: d.name, bonus: stats.length ? stats.join('  ·  ') : d.short, color: d.color, icon: d.icon };
    });
    this.gear.set(rows);
  }

  /** Values available from RunState alone (also between rooms). */
  private pollRun(run: RunState, ctx: GameContext | null, dt: number): void {
    const sector = ctx?.world?.sector ?? SECTORS[run.sectorIndex];
    this.status.setSector(sector?.name ?? `SECTOR ${run.sectorIndex + 1}`, run.sectorIndex, SECTORS_PER_RUN);
    this.status.setRoute(run.route, run.nodeId);
    this.status.setScrap(run.scrap);
    this.synergyTimer -= dt;
    if (this.synergyTimer <= 0) {
      this.synergyTimer = 0.3;
      let ids: readonly SynergyId[] = [];
      try {
        ids = ctx?.effects?.activeSynergies ?? run.inventory.activeSynergies(run.mech.kitTags);
      } catch {
        ids = [];
      }
      this.status.setSynergies(ids);
    }
    if (!ctx) {
      // Between rooms: show what the run stored.
      this.vitals.set({ hp: run.hp, maxHp: Math.max(this.maxHp, run.hp), shield: run.shield, maxShield: Math.max(run.shield, 1), dashCount: 1, dashReady: 1 }, this.first);
    }
  }

  private pollContext(ctx: GameContext, run: RunState, dt: number, reduced: boolean): void {
    const p = ctx.player;
    const st = p.stats;
    this.maxHp = Math.max(1, st.maxHp);
    this.hpFrac = p.alive ? p.hp / this.maxHp : 0;
    const dashMax = Math.max(0.05, p.movement.dashCooldown * st.dashCooldown);
    this.vitals.set(
      {
        hp: p.hp,
        maxHp: st.maxHp,
        shield: p.shield,
        maxShield: st.maxShield,
        dashCount: Math.max(1, st.airDashes),
        dashReady: p.dashCooldownRemaining > 0 ? 1 - p.dashCooldownRemaining / dashMax : 1,
      },
      this.first,
    );

    // Abilities.
    const ab = ctx.abilities;
    const cd = Math.max(0, ab.signatureCooldown);
    const cdMax = Math.max(0.01, ab.signatureCooldownMax);
    this.sig.set({ progress: cd > 0 ? 1 - cd / cdMax : 1, ready: cd <= 0, active: ab.signatureActive, text: cd >= 10 ? String(Math.ceil(cd)) : cd.toFixed(1) }, reduced);
    const charge = Phaser.Math.Clamp(p.ultCharge / ULTIMATE_COST, 0, 1);
    this.ult.set({ progress: ab.ultimateActive ? 1 : charge, ready: ab.ultimateReady || charge >= 1, active: ab.ultimateActive, text: `${Math.floor(charge * 100)}%` }, reduced);

    // Weapons.
    const runtimes = p.weapons;
    const inv = run.inventory.weapons;
    const slot = (i: number): WeaponSlotState => {
      const rt = runtimes[i];
      const id = (rt?.def.id ?? inv[i]?.defId ?? null) as WeaponId | null;
      return { id, name: rt?.def.name };
    };
    const w = p.activeWeapon();
    this.weapons.set({
      slots: [slot(0), slot(1)],
      active: p.activeWeaponIndex,
      heat: w?.heat ?? 0,
      overheated: !!w?.overheated,
      overheatRemaining: w?.overheatRemaining ?? 0,
      charge: w && w.def.behavior === 'charge' ? w.charge : -1,
    });

    this.pollGear(run);

    // Boss bar (also covers a boss that spawned before the HUD subscribed).
    const boss = ctx.bosses?.active ?? null;
    if (boss && boss.alive) {
      const s = { name: boss.name, title: boss.title, hp: boss.hp, maxHp: boss.maxHp, armor: boss.armorHp, phase: boss.phase, phaseCount: boss.phaseCount };
      if (!this.boss.isShown) this.boss.show(s);
      else this.boss.set(s);
    } else if (this.boss.isShown && !boss) this.boss.hide();

    this.tracker.set(ctx.world?.challenge ?? null);

    // Interaction prompt.
    const f = ctx.interact?.focused ?? null;
    if (f && p.alive) {
      const enabled = f.enabled ? f.enabled() : true;
      this.prompt.set(f.prompt, f.altPrompt ?? null, enabled);
    } else this.prompt.set(null);

    this.updateReticle(ctx, dt);
  }

  /** No room loaded (transition): hide room-bound widgets. */
  private idle(): void {
    this.prompt.set(null);
    this.reticle.setVisible(false);
    if (this.boss.isShown) this.boss.hide();
  }

  // ───────────────────────────── Reticle ─────────────────────────────

  /** Screen-space crosshair at the aim point; hidden while the game is paused. */
  private updateReticle(ctx: GameContext, dt: number): void {
    const playing = this.scene.isActive(SCENES.GAME) && ctx.player.alive;
    if (!playing) {
      this.reticle.setVisible(false);
      return;
    }
    const cam = ctx.scene.cameras.main;
    const aim = ctx.input.pointerWorld;
    const x = (aim.x - cam.worldView.x) * cam.zoom;
    const y = (aim.y - cam.worldView.y) * cam.zoom;
    this.reticleKick = Math.max(0, this.reticleKick - dt * 7);
    const w = ctx.player.activeWeapon();
    const tint = w?.overheated ? COLORS.threat : (w?.heat ?? 0) > 0.75 ? COLORS.heatBar : this.accent;
    this.reticle
      .setVisible(true)
      .setPosition(x, y)
      .setScale(1 + this.reticleKick * 0.28)
      .setRotation(this.reticleKick * 0.25)
      .setTint(tint);
  }

  private fallbackReticle(): string {
    const key = 'ui_reticle_fb';
    if (this.textures.exists(key)) return key;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.lineStyle(2, 0xffffff, 1);
    g.strokeCircle(24, 24, 12);
    g.lineBetween(24, 2, 24, 14);
    g.lineBetween(24, 34, 24, 46);
    g.lineBetween(2, 24, 14, 24);
    g.lineBetween(34, 24, 46, 24);
    g.fillStyle(0xffffff, 1);
    g.fillCircle(24, 24, 2);
    g.generateTexture(key, 48, 48);
    g.destroy();
    return key;
  }
}

/** '-20% dash cooldown' → '-20% DASH CD' (fits the HUD's equipment rows). */
function shortStat(line: string): string {
  return line
    .replace(/cooldown/gi, 'CD')
    .replace(/movement speed|move speed/gi, 'MOVE')
    .replace(/max shield/gi, 'SHIELD')
    .replace(/max hp/gi, 'HP')
    .replace(/shield delay/gi, 'SH. DELAY')
    .replace(/knockback resist(ance)?/gi, 'KB RES')
    .replace(/damage/gi, 'DMG')
    .replace(/jump power/gi, 'JUMP')
    .toUpperCase();
}
